import React, { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GameResultService } from '../../services/game/GameResultService'
import { GameResult } from '../../types/database'
import { useAuth } from '../../contexts/AuthContext'
import { Trophy, Medal, Award, Clock, RefreshCw, TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface RealTimeLeaderboardProps {
  gameId?: string
  classId?: string
  limit?: number
  autoRefresh?: boolean
  refreshInterval?: number // milliseconds
  showCurrentUser?: boolean
}

interface LeaderboardEntry {
  result: GameResult
  rank: number
  previousRank?: number
  isCurrentUser: boolean
}

/**
 * Real-time Leaderboard Component
 * Hiển thị bảng xếp hạng cập nhật real-time, sắp xếp theo điểm số
 */
export const RealTimeLeaderboard: React.FC<RealTimeLeaderboardProps> = ({
  gameId,
  classId,
  limit = 20,
  autoRefresh = true,
  refreshInterval = 3000, // 3 giây
  showCurrentUser = true
}) => {
  const { user: currentUser } = useAuth()
  const [leaderboard, setLeaderboard] = useState<GameResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())
  const [previousLeaderboard, setPreviousLeaderboard] = useState<GameResult[]>([])

  // Load leaderboard từ server
  const loadLeaderboard = async () => {
    try {
      setError(null)
      const results = await GameResultService.getGameLeaderboard(
        gameId,
        classId,
        limit * 2, // Lấy nhiều hơn để có thể filter
        'score', // Sort by score
        'won' // Chỉ lấy những người thắng
      )

      // Sort lại theo điểm số (cao nhất trước), nếu cùng điểm thì thời gian nhanh hơn trước
      const sorted = results.sort((a, b) => {
        if (a.percentage !== b.percentage) {
          return b.percentage - a.percentage // Điểm cao hơn xếp trước
        }
        return a.timeSpent - b.timeSpent // Cùng điểm thì nhanh hơn xếp trước
      })

      setPreviousLeaderboard(leaderboard)
      setLeaderboard(sorted.slice(0, limit))
      setLastUpdate(new Date())
    } catch (err: any) {
      console.error('Error loading leaderboard:', err)
      setError(err.message || 'Không thể tải bảng xếp hạng')
    } finally {
      setLoading(false)
    }
  }

  // Load lần đầu
  useEffect(() => {
    loadLeaderboard()
  }, [gameId, classId, limit])

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(() => {
      loadLeaderboard()
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [autoRefresh, refreshInterval, gameId, classId, limit])

  // Tính toán rank và so sánh với rank trước
  const rankedEntries = useMemo(() => {
    const entries: LeaderboardEntry[] = leaderboard.map((result, index) => {
      const rank = index + 1
      
      // Tìm rank trước đó
      const previousResult = previousLeaderboard.find(r => r.id === result.id)
      const previousRank = previousResult 
        ? previousLeaderboard.indexOf(previousResult) + 1
        : undefined

      return {
        result,
        rank,
        previousRank,
        isCurrentUser: result.userId === currentUser?.id
      }
    })

    return entries
  }, [leaderboard, previousLeaderboard, currentUser?.id])

  // Format time
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Get rank change indicator
  const getRankChange = (entry: LeaderboardEntry) => {
    if (!entry.previousRank) return null
    if (entry.rank < entry.previousRank) {
      return { icon: <TrendingUp className="w-4 h-4 text-green-500" />, text: `+${entry.previousRank - entry.rank}`, color: 'text-green-600' }
    }
    if (entry.rank > entry.previousRank) {
      return { icon: <TrendingDown className="w-4 h-4 text-red-500" />, text: `-${entry.rank - entry.previousRank}`, color: 'text-red-600' }
    }
    return { icon: <Minus className="w-4 h-4 text-gray-400" />, text: '—', color: 'text-gray-400' }
  }

  // Get rank icon
  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-600" />
    return <span className="text-gray-600 font-bold text-lg">#{rank}</span>
  }

  if (loading && leaderboard.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
        <p className="text-gray-600">Đang tải bảng xếp hạng...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadLeaderboard}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Thử lại
        </button>
      </div>
    )
  }

  if (leaderboard.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="text-6xl mb-4">🏆</div>
        <h3 className="text-xl font-bold text-gray-700 mb-2">Chưa có kết quả</h3>
        <p className="text-gray-500">Hãy chơi game để xuất hiện trong bảng xếp hạng!</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-xl shadow-lg p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">🏆 Bảng Xếp Hạng Real-time</h2>
              <p className="text-sm text-white/90">
                Top {leaderboard.length} • Cập nhật: {lastUpdate.toLocaleTimeString('vi-VN')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {autoRefresh && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-sm font-medium">Live</span>
              </div>
            )}
            <button
              onClick={loadLeaderboard}
              className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors"
              title="Làm mới"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase">Hạng</th>
                <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase">Người chơi</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase">Điểm</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase">Thời gian</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase">Thay đổi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <AnimatePresence>
                {rankedEntries.map((entry) => {
                  const rankChange = getRankChange(entry)
                  
                  return (
                    <motion.tr
                      key={entry.result.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className={`hover:bg-gray-50 transition-colors ${
                        entry.isCurrentUser ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      {/* Rank */}
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          {getRankIcon(entry.rank)}
                          {entry.rank <= 3 && (
                            <motion.div
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            >
                              ✨
                            </motion.div>
                          )}
                        </div>
                      </td>

                      {/* User */}
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs ${
                            entry.isCurrentUser 
                              ? 'bg-gradient-to-br from-blue-500 to-purple-600 ring-2 ring-blue-300' 
                              : 'bg-gradient-to-br from-gray-400 to-gray-500'
                          }`}>
                            {entry.result.userId.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-xs">
                              {entry.isCurrentUser ? 'Bạn' : `User ${entry.result.userId.slice(-4)}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              #{entry.result.attempts}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Score */}
                      <td className="px-2 py-2 text-center">
                        <div className="inline-flex flex-col items-center">
                          <div className="text-lg font-bold text-gray-900">
                            {entry.result.percentage.toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500">
                            {entry.result.score}/{entry.result.maxScore}
                          </div>
                        </div>
                      </td>

                      {/* Time */}
                      <td className="px-2 py-2 text-center">
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-full">
                          <Clock className="w-3 h-3 text-blue-600" />
                          <span className="font-mono font-bold text-blue-700 text-xs">
                            {formatTime(entry.result.timeSpent)}
                          </span>
                        </div>
                      </td>

                      {/* Rank Change */}
                      <td className="px-2 py-2 text-center">
                        {rankChange ? (
                          <div className={`flex items-center justify-center gap-1 ${rankChange.color}`}>
                            {rankChange.icon}
                            <span className="text-xs font-semibold">{rankChange.text}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">Mới</span>
                        )}
                      </td>
                    </motion.tr>
                  )
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>

      {/* Current User Highlight */}
      {showCurrentUser && currentUser && !rankedEntries.some(e => e.isCurrentUser) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <p className="text-blue-700">
            💡 Bạn chưa có trong top {limit}. Hãy chơi game để xuất hiện trong bảng xếp hạng!
          </p>
        </div>
      )}
    </div>
  )
}

